import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CustomError } from '@api/interceptors';
import { RoutesPath } from '@shared/constants';
import { AuthData } from '@shared/services/auth';
import { LogsData } from '@shared/services/logs';
import { catchError, finalize, from, Observable, shareReplay, switchMap, throwError } from 'rxjs';

/**
 * HTTP interceptor standardizing error handling for all HTTP responses.
 *
 * @remarks
 * - Catches all HTTP errors and transforms them into CustomError format
 * - Detects status 0 errors indicating network/connectivity issues
 * - Re-throws errors for downstream handling in services/components
 *
 * @example
 * ```
 * // In app.config.ts
 * provideHttpClient(withInterceptorsFromDi());
 *
 * // Automatic error transformation
 * export class UserComponent {
 *   private userService = inject(UserService);
 *
 *   loadUser(userId: string): void {
 *     this.userService.read(userId).subscribe({
 *       error: (err: CustomError) => {
 *         console.error('Status:', err.status);
 *         console.error('Original:', err.server);
 *       }
 *     });
 *   }
 * }
 * ```
 */
@Injectable()
export class ResponseInterceptor implements HttpInterceptor {
  private readonly logs = inject(LogsData);
  private readonly authData = inject(AuthData);
  private readonly router = inject(Router);

  // Shared observable to mutualize concurrent 401s into a single logout/navigate run.
  private logoutInFlight$: Observable<boolean> | null = null;

  /**
   * Intercepts HTTP responses to standardize error handling.
   *
   * @param req - Outgoing HTTP request
   * @param next - Next handler in the interceptor chain
   * @returns Observable of HTTP event or CustomError on failure
   *
   * @remarks
   * - Status 0 indicates network connectivity or CORS issues.
   * - Status 401 triggers automatic logout and redirect to login,
   *   except on the logout endpoint itself (to avoid infinite recursion).
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((e) => {
        if (e instanceof HttpErrorResponse) {
          if (e.status === 0) {
            this.logs.error('ResponseInterceptor', `Server access problems`);
          }

          if (e.status === 401 && !this.isLogoutRequest(req)) {
            return this.handleUnauthorized(e);
          }
        }
        return this.toCustomError(e);
      })
    );
  }

  private handleUnauthorized(e: HttpErrorResponse): Observable<never> {
    this.logoutInFlight$ ??= this.authData.logout().pipe(
      switchMap(() => from(this.router.navigate([RoutesPath.LOGIN]))),
      finalize(() => (this.logoutInFlight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.logoutInFlight$.pipe(switchMap(() => this.toCustomError(e)));
  }

  private isLogoutRequest(req: HttpRequest<unknown>): boolean {
    return req.url.endsWith('/logout');
  }

  private toCustomError(e: unknown): Observable<never> {
    const { status, error } = e as CustomError;
    return throwError(
      () =>
        ({
          server: e as HttpErrorResponse,
          error,
          status,
        }) as CustomError
    );
  }
}
